import './Breadcrumb.scss';

import cn from 'classnames';
import React, { useCallback, useMemo } from 'react';
import { NavLink, useHistory } from 'react-router-dom';

import Button from '@/common/components/atoms/Button/Button';
import { ROUTE_HOME, ROUTE_NODES_CATEGORY } from '@/common/constants';
import { useHistoryContext } from '@/common/contexts/HistoryContext/HistoryContext';
import useNodesTree from '@/common/hooks/nodes/useNodesTree';
import useCategories from '@/common/hooks/useFlattenCategories';

import {
  BreadcrumbDefaultProps,
  BreadcrumbPropTypes,
} from './Breadcrumb.proptypes';

const Breadcrumb = ({
  backIcon,
  breadcrumb,
  className,
  depth,
  navigable,
  route,
  linkEnableIndex,
}) => {
  let uri = route;
  const history = useHistory();
  const { historyQueue } = useHistoryContext();

  const { data: tree } = useNodesTree({ depth });
  const { flattenCategories } = useCategories();
  const flatTreeMap = useMemo(
    () => tree && flattenCategories({ categories: tree, enabled: !!depth }),
    [tree, flattenCategories, depth],
  );
  const getLink = (linkDepth) =>
    [
      `${ROUTE_NODES_CATEGORY}`,
      ...breadcrumb
        .filter((item) => item.depth <= linkDepth)
        .map((item) => flatTreeMap?.get(item.id)),
    ].join('/');

  const handleCloseRedirect = useCallback(() => {
    // Find the first entry in historyQueue that does not contain "/product/"
    // it's to handle the case of switching reference in product page.
    const nonProductPath = historyQueue.find(
      (entry) => !entry.pathname.includes('/product/'),
    );
    // If a non-product path is found, redirect to that path
    // Otherwise, redirect to the home route
    if (nonProductPath) {
      history.push(nonProductPath);
    } else {
      history.push(ROUTE_HOME);
    }
  }, [history, historyQueue]);

  return (
    <nav className={cn('breadcrumb', className)} data-qa-id="breadcrumb">
      <ul className="breadcrumb__list" data-qa-id="list">
        {backIcon && !!depth && (
          <li
            className="breadcrumb__item breadcrumb__item--back"
            data-qa-id="breadcrumb-item-back"
            key="breadcrumb-item-back"
          >
            <Button
              className="button--back"
              context="breadcrumb-back"
              onClick={handleCloseRedirect}
            >
              {backIcon}
            </Button>
          </li>
        )}
        {breadcrumb &&
          breadcrumb.map((item, i) => {
            if (navigable) {
              uri += `/${item.path}`;
              return (
                <li
                  className="breadcrumb__item"
                  data-qa-id="breadcrumb-item"
                  key={`breadcrumb-item-${item?.id}`}
                >
                  <NavLink
                    className="breadcrumb__link"
                    data-qa-id="breadcrumb-item-link"
                    to={uri}
                  >
                    {item.label}
                  </NavLink>
                </li>
              );
            }

            if (i < linkEnableIndex) {
              return (
                <li
                  className="breadcrumb__item"
                  data-qa-id="breadcrumb-item"
                  key={`breadcrumb-item-${item?.id}`}
                >
                  <span className="breadcrumb__link  breadcrumb__link--disabled">
                    {item.label}
                  </span>
                </li>
              );
            }

            return (
              <li
                className="breadcrumb__item"
                data-qa-id="breadcrumb-item"
                key={`breadcrumb-item-${item?.id}`}
              >
                <NavLink
                  className={cn('breadcrumb__link')}
                  data-qa-id="breadcrumb-item-link"
                  to={getLink(i + 1)}
                >
                  {item.label}
                </NavLink>
              </li>
            );
          })}
      </ul>
    </nav>
  );
};

Breadcrumb.defaultProps = {
  ...BreadcrumbDefaultProps,
};

Breadcrumb.propTypes = {
  ...BreadcrumbPropTypes,
};

export default Breadcrumb;
